// Live's Arrangement selection: a span of TIME across lanes, not a set of clips.
//
// In Live a click on a clip's title bar selects the clip, and a click or drag
// anywhere else — the clip's body, or the empty lane — selects time: a point
// (the insert marker) or a span over one or more tracks. Copy, cut, paste,
// duplicate, delete and split then act on whatever lies inside the span, so
// half a clip, a beat out of a bar, or a bar with silence in it are all things
// you can copy.
//
// The rules this file keeps:
//
// 1. THE SPAN IS THE UNIT. A copied span is its material AND its length: a
//    bar holding a half-bar clip duplicates with a one-bar period, and lands
//    replacing the whole bar it lands on, silence included.
//
// 2. CONTENT STAYS PUT. A clip the span cuts through is cropped to it with
//    lib/live-edits.ts `cropData` — the audio (or the notes) inside the span
//    is the audio copied, never the clip's head slid along.
//
// 3. NOTHING IS SNAPPED HERE. The span is snapped where it was drawn (the
//    view, on the grid you see); an operation lands exactly where it is
//    told. Snapping the result again is what made a quarter-beat clip
//    duplicate onto itself.

import type { CanonicalClipState, CanonicalRoom, CanonicalTrackState } from "@shared/sync/state.js";
import { arrangementClipsOfTrack } from "@shared/sync/state.js";
import { ASSERT_EPS } from "@shared/sync/canonical.js";
import type { Intent } from "@shared/sync/intents.js";
import { clipDataOf, copyable, mintTempId } from "@web-shared/lib/edits";
import { asGesture, clearSpans, cropData, splitClipsAtPoints, SLIVER_BEATS, type LaneSpan } from "@web-shared/lib/live-edits";

/** A selected span: beats, half-open [start, end), over these lanes. */
export interface TimeSpan {
  start: number;
  end: number;
  laneIds: string[];
}

/**
 * What a copy carries. `clips` are canonical records (rule 1 of
 * seedbed-web's clip-ops: a record, not an id — a cut clip is gone from the
 * room by the time it is pasted), cropped to the span when one was copied.
 *
 * `start` is where the block's time begins and `length` how much time it is:
 * a paste lands each clip at the same distance from where the block lands.
 * `span` is true for a copied time selection — the block replaces all of
 * [at, at + length) on its lanes — and false for copied clips, which
 * overwrite only where they land.
 */
export interface ClipBlock {
  clips: CanonicalClipState[];
  start: number;
  length: number;
  /** The lanes the block was taken from, in display order. */
  laneIds: string[];
  span: boolean;
}

function clipKind(clip: CanonicalClipState): "audio" | "midi" {
  return (clip.clipKind ?? "audio") === "midi" ? "midi" : "audio";
}

function laneKind(track: CanonicalTrackState): "audio" | "midi" {
  return (track.trackKind ?? "audio") === "midi" ? "midi" : "audio";
}

/** The Set's lanes in display order. */
export function lanesInOrder(room: CanonicalRoom): CanonicalTrackState[] {
  return [...room.tracks].sort((a, b) => a.ord - b.ord);
}

function orderIds(room: CanonicalRoom, ids: Iterable<string>): string[] {
  const want = new Set(ids);
  return lanesInOrder(room).filter((t) => want.has(t.id)).map((t) => t.id);
}

/** A span worth acting on — not a click's zero-width point. */
export function isSpan(sel: TimeSpan | null | undefined): sel is TimeSpan {
  return sel != null && sel.end - sel.start > ASSERT_EPS && sel.laneIds.length > 0;
}

/** Timeline clips on the span's lanes that overlap it. */
export function clipsInSpan(room: CanonicalRoom, sel: TimeSpan): CanonicalClipState[] {
  return sel.laneIds.flatMap((laneId) =>
    arrangementClipsOfTrack(room, laneId).filter(
      (c) => c.startTime < sel.end - ASSERT_EPS && c.startTime + c.duration > sel.start + ASSERT_EPS,
    ),
  );
}

/** Clips lying wholly inside the span — what Live highlights as selected. */
export function clipsWithinSpan(room: CanonicalRoom, sel: TimeSpan): CanonicalClipState[] {
  return clipsInSpan(room, sel).filter(
    (c) => c.startTime >= sel.start - ASSERT_EPS && c.startTime + c.duration <= sel.end + ASSERT_EPS,
  );
}

/**
 * Copy the span: every clip it touches, cropped to it. `skipped` counts
 * clips that could not be carried — nothing to copy from (`copyable`), or a
 * cut the wire can't state (`cropData`: a transposed unwarped clip's head).
 */
export function copySpan(
  room: CanonicalRoom,
  sel: TimeSpan,
  tempo: number,
): { block: ClipBlock; skipped: number } {
  const clips: CanonicalClipState[] = [];
  let skipped = 0;
  for (const c of clipsInSpan(room, sel)) {
    if (!copyable(c)) {
      skipped += 1;
      continue;
    }
    const from = Math.max(c.startTime, sel.start);
    const to = Math.min(c.startTime + c.duration, sel.end);
    if (to - from < SLIVER_BEATS) continue;
    const whole = Math.abs(from - c.startTime) <= ASSERT_EPS && Math.abs(to - (c.startTime + c.duration)) <= ASSERT_EPS;
    const data = whole ? clipDataOf(c) : cropData(clipDataOf(c), from, to, tempo);
    if (data === null) {
      skipped += 1;
      continue;
    }
    clips.push({ ...c, ...data });
  }
  return {
    block: { clips, start: sel.start, length: sel.end - sel.start, laneIds: orderIds(room, sel.laneIds), span: true },
    skipped,
  };
}

/** Copied clips as a block: their own extent, their own lanes. */
export function blockOfClips(room: CanonicalRoom, clips: readonly CanonicalClipState[]): ClipBlock {
  const usable = clips.filter(copyable);
  const start = usable.reduce((m, c) => Math.min(m, c.startTime), Infinity);
  const end = usable.reduce((m, c) => Math.max(m, c.startTime + c.duration), -Infinity);
  return {
    clips: usable,
    start: Number.isFinite(start) ? start : 0,
    length: Number.isFinite(start) ? end - start : 0,
    laneIds: orderIds(room, usable.map((c) => c.trackId)),
    span: false,
  };
}

/**
 * Where each of the block's lanes lands when its top lane is aimed at
 * `targetLaneId`: the same distance down the Set. A lane that runs off the end
 * or is the wrong kind for what the block carries on it maps to null — its
 * clips are skipped and counted, never folded onto another lane.
 */
export function blockLaneMap(
  room: CanonicalRoom,
  block: ClipBlock,
  targetLaneId: string,
): Map<string, string | null> {
  const lanes = lanesInOrder(room);
  const indexOf = new Map(lanes.map((t, i) => [t.id, i]));
  const top = block.laneIds.reduce((m, id) => Math.min(m, indexOf.get(id) ?? Infinity), Infinity);
  const target = indexOf.get(targetLaneId);
  const map = new Map<string, string | null>();
  for (const id of block.laneIds) {
    const from = indexOf.get(id);
    if (from === undefined || target === undefined || !Number.isFinite(top)) {
      map.set(id, null);
      continue;
    }
    const lane = lanes[target + (from - top)];
    // The lane must take what the block carries on it. An empty source lane
    // carries nothing, so any lane will do — but it still clears its span.
    const source = room.tracks.find((t) => t.id === id);
    const carried = block.clips.find((c) => c.trackId === id);
    const wanted = carried ? clipKind(carried) : source ? laneKind(source) : undefined;
    map.set(id, lane && (wanted === undefined || laneKind(lane) === wanted) ? lane.id : null);
  }
  return map;
}

/**
 * The block landed at `at`: one create per clip, the clearing it needs (a
 * span block clears all of its time on every lane it lands on), and what
 * the result covers — the paste's new time selection.
 */
export function placeBlock(
  block: ClipBlock,
  at: number,
  laneMap: ReadonlyMap<string, string | null>,
): { placements: Intent[]; clears: LaneSpan[]; landed: TimeSpan | null; skipped: number } {
  const placements: Intent[] = [];
  let skipped = 0;
  const begin = Math.max(0, at);
  for (const c of block.clips) {
    const laneId = laneMap.get(c.trackId) ?? null;
    if (laneId === null) {
      skipped += 1;
      continue;
    }
    placements.push({
      kind: "upsertClip",
      id: null,
      tempId: mintTempId("paste"),
      trackId: laneId,
      baseRev: null,
      clip: { ...clipDataOf(c), startTime: begin + (c.startTime - block.start) },
      audioKey: c.audioKey,
    });
  }
  const lanes = block.laneIds.map((id) => laneMap.get(id) ?? null).filter((id): id is string => id !== null);
  const clears = block.span ? lanes.map((trackId) => ({ trackId, from: begin, to: begin + block.length })) : [];
  const landed = lanes.length > 0 && block.length > ASSERT_EPS ? { start: begin, end: begin + block.length, laneIds: lanes } : null;
  return { placements, clears, landed, skipped };
}

/** Live's Delete on a time selection: the span emptied, clips cut round it. */
export function clearSpan(room: CanonicalRoom, sel: TimeSpan, tempo: number): Intent[] | null {
  const spans = sel.laneIds.map((trackId) => ({ trackId, from: sel.start, to: sel.end }));
  const intents = clearSpans(room, spans, new Set(), tempo);
  return intents === null ? null : asGesture(intents);
}

/** Live's ⌘E on a time selection: every clip it crosses cut at both edges. */
export function splitAtSpan(room: CanonicalRoom, sel: TimeSpan, tempo: number): Intent[] {
  const ids = clipsInSpan(room, sel).map((c) => c.id);
  return splitClipsAtPoints(room, ids, [sel.start, sel.end], tempo);
}

/** The span a set of clips covers, on their lanes. */
export function spanOfClips(room: CanonicalRoom, clips: readonly CanonicalClipState[]): TimeSpan | null {
  const timeline = clips.filter((c) => c.slot === undefined);
  if (timeline.length === 0) return null;
  return {
    start: Math.min(...timeline.map((c) => c.startTime)),
    end: Math.max(...timeline.map((c) => c.startTime + c.duration)),
    laneIds: orderIds(room, timeline.map((c) => c.trackId)),
  };
}

// ---------------------------------------------------------------------------
// The grid
// ---------------------------------------------------------------------------

/** Live's adaptive grid lines sit about this far apart at its default width. */
export const ADAPTIVE_GRID_PX = 16;
/** ⌘1 / ⌘2 halve and double it, within these bounds. */
export const GRID_DENSITY_MIN = 0.25;
export const GRID_DENSITY_MAX = 4;

/**
 * Live's zoom-adaptive grid: the finest musical division — down to a 64th
 * note — whose lines are at least `ADAPTIVE_GRID_PX / density` apart at this
 * zoom. Half bars, bars and their multiples once a beat is too narrow. Zoomed in on a
 * hi-hat, a sixteenth is something you can place; zoomed out on a song, a
 * click lands on a bar.
 */
export function adaptiveGrid(pxPerBeat: number, beatsPerBar: number, density = 1): number {
  if (!(pxPerBeat > 0)) return beatsPerBar;
  const minPx = ADAPTIVE_GRID_PX / Math.max(GRID_DENSITY_MIN, Math.min(GRID_DENSITY_MAX, density));
  const half = beatsPerBar % 2 === 0 && beatsPerBar > 2 ? [beatsPerBar / 2] : [];
  const candidates = [1 / 16, 1 / 8, 1 / 4, 1 / 2, 1, ...half, ...[1, 2, 4, 8, 16, 32, 64].map((n) => n * beatsPerBar)];
  return candidates.find((c) => c * pxPerBeat >= minPx) ?? 128 * beatsPerBar;
}

/** A grid size as Live names it in the corner of the Arrangement: 1/16, 1 Bar… */
export function gridLabel(grid: number, beatsPerBar: number): string {
  if (grid <= 0) return "Off";
  if (grid >= beatsPerBar && Math.abs(grid / beatsPerBar - Math.round(grid / beatsPerBar)) < 1e-9) {
    const bars = Math.round(grid / beatsPerBar);
    return bars === 1 ? "1 Bar" : `${bars} Bars`;
  }
  // A beat is a quarter note.
  const denom = Math.round(4 / grid);
  return Math.abs(4 / grid - denom) < 1e-9 ? `1/${denom}` : `${grid} beats`;
}
